import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { useRef, useEffect, useState, useContext } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { FcGoogle } from 'react-icons/fc';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '../../../v2/js/components/button';
import { HowItWorksDialog } from '../../../v2/js/components/how-it-works-dialog';
import { localStore } from '../../../v2/js/utils/storage';
import { AuthService, UserRegistration, UserTypeEnum } from '../../api';
import { FormCheckboxField } from '../../app/components/FormCheckboxField';
import { FormFeedback } from '../../app/components/FormFeedback';
import { FormInputMaskField } from '../../app/components/FormInputMaskField';
import { FormTextField } from '../../app/components/FormTextField';
import { Logo } from '../../app/components/Logo';
import { PasswordField } from '../../app/components/PasswordField';
import { SessionContext } from '../../app/contexts/SessionContext';
import { useDocumentTitle } from '../../app/hooks/use-document-title';
import { setFormErrorsFromApiErrors } from '../../utils/forms';
import { strongPasswordZodSchema } from '../../utils/security';

const baseValidationSchema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email({
    message: "That email doesn't look quite right.",
  }),
  first_name: z.string().min(1, { message: 'First Name is required' }),
  last_name: z.string().min(1, { message: 'Last Name is required' }),
  phone_number: z.string().min(1, { message: 'Phone Number is required' }),
  password1: strongPasswordZodSchema,
  password2: z.string().min(1, { message: 'Confirm Password is required' }),
  text_opt_in: z.boolean(),
});

const providerValidationSchema = baseValidationSchema.refine(
  ({ password1, password2 }) => password1 === password2,
  {
    path: ['password2'],
    message: "Your passwords don't match.",
  }
);

const repValidationSchema = baseValidationSchema
  .extend({
    healthcare_entity_rep__healthcare_entity__name: z
      .string()
      .min(1, { message: 'Healthcare Entity Name is required' }),
    healthcare_entity_rep__title: z.string().min(1, { message: 'Job Title is required' }),
  })
  .refine(({ password1, password2 }) => password1 === password2, {
    path: ['password2'],
    message: "Your passwords don't match.",
  });

export const AccountSetup = ({
  userType,
  documentTitle,
}: {
  userType: UserTypeEnum;
  documentTitle: string;
}) => {
  useDocumentTitle(documentTitle);
  const { setSessionExists } = useContext(SessionContext);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const referralProvider =
    localStore.get('attributionReferralProvider') || searchParams.get('referral_provider');
  const {
    handleSubmit,
    formState: { errors },
    setError,
    register,
    setValue,
  } = useForm({
    resolver: zodResolver(
      userType === UserTypeEnum.HEALTHCARE_ENTITY_REP
        ? repValidationSchema
        : providerValidationSchema
    ),
  });

  // Capture UTM parameters on component mount
  useEffect(() => {
    const utmParams = Array.from(searchParams.entries()).reduce(
      (acc, [key, value]) => {
        if (key.startsWith('utm_')) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    // Store UTM parameters if any exist
    if (Object.keys(utmParams).length > 0) {
      localStore.set('attributionSignUpCampaign', JSON.stringify(utmParams));
    }
  }, [searchParams]);

  // Capture referral provider and program id when present in URL
  useEffect(() => {
    const referralProvider = searchParams.get('referral_provider');
    const programId = searchParams.get('program_id');
    if (referralProvider && referralProvider.trim().length > 0) {
      localStore.set('attributionReferralProvider', referralProvider);
    }
    if (programId && programId.trim().length > 0) {
      localStore.set('attributionProgramID', programId);
    }
  }, [searchParams]);

  useEffect(() => {
    const allowedFields = ['email', 'first_name', 'last_name', 'phone_number'] as const;
    type PrefillKey = (typeof allowedFields)[number];
    const prefillMap: Array<[PrefillKey, string | null]> = [
      ['email', searchParams.get('email')],
      ['first_name', searchParams.get('first_name')],
      ['last_name', searchParams.get('last_name')],
      ['phone_number', searchParams.get('phone_number')],
    ];

    prefillMap.forEach(([field, value]) => {
      if (value && value.trim().length > 0) {
        setValue(field, value, { shouldValidate: true, shouldDirty: true });
      }
    });
  }, [searchParams, setValue]);

  const mutationRegister = useMutation({
    mutationFn: async (data: UserRegistration) => {
      localStore.remove('session');
      const response = await AuthService.authRegistrationCreate({ requestBody: data });
      localStore.set('authLastSigninEmail', data.email);
      return response;
    },
  });

  const registerProvider = ({ first_name, last_name, phone_number, ...data }) => {
    const swagScanId = localStore.get('attributionSwagScanId') || localStore.get('swag_scan_id');
    const signUpCampaign = localStore.get('attributionSignUpCampaign');
    const referralProvider = localStore.get('attributionReferralProvider');
    const programId = localStore.get('attributionProgramID');
    const metadata: Record<string, string> = {};
    if (swagScanId) {
      metadata.swag_scan_id = swagScanId;
    }
    if (signUpCampaign) {
      metadata.utm_params = JSON.parse(signUpCampaign);
    }
    return mutationRegister.mutateAsync({
      ...data,
      first_name: first_name,
      last_name: last_name,
      phone_number: phone_number,
      user_type: userType,
      ...(referralProvider && { referral_provider: referralProvider }),
      ...(programId && { program_id: programId }),
      ...(Object.keys(metadata).length > 0 && { metadata }),
    } as UserRegistration);
  };

  const registerRep = ({
    first_name,
    last_name,
    phone_number,
    healthcare_entity_rep__healthcare_entity__name,
    healthcare_entity_rep__title,
    ...data
  }) =>
    mutationRegister.mutateAsync({
      ...data,
      first_name: first_name,
      last_name: last_name,
      phone_number: phone_number,
      healthcare_entity_rep: {
        healthcare_entity: {
          name: healthcare_entity_rep__healthcare_entity__name,
        },
        title: healthcare_entity_rep__title,
      },
      user_type: userType,
    } as UserRegistration);

  // Callback for registration form submission
  const onSubmit = async (data) => {
    try {
      if (!executeRecaptcha) {
        console.warn('Recaptcha not ready yet');
        // Decide: Block submission or allow proceed?
        // Best practice: Fail safely or wait. Here we proceed but log warning.
      }

      let recaptchaToken = '';

      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('registration');
        console.log('Recaptcha token:', recaptchaToken);
      }

      const payload = {
        ...data,
        recaptcha_token: recaptchaToken, // Backend must expect this field
      };
      let response;
      if (userType === UserTypeEnum.HEALTHCARE_ENTITY_REP) {
        response = await registerRep(payload);
      } else {
        response = await registerProvider(payload);
      }

      // Push event to GTM
      if (window.TAG_GOOGLE_TAG_MANAGER_ID) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'registration_complete',
          user_type: userType,
          registration_method: 'manual',
        });
      }

      // If the backend logged the user in directly (HCO sourced provider with pre-verified
      // email), the response contains user data rather than a verification detail message.
      // Navigate home instead of asking them to check their email.
      if (response?.id) {
        setSessionExists();
        navigate('/');
      } else {
        navigate('/register/email-confirmation');
      }
    } catch (errors) {
      setFormErrorsFromApiErrors(errors, setError);
    }
  };

  return (
    <div className="tw:flex-1 tw:w-full tw:min-h-screen tw:flex tw:flex-col tw:p-4 tw:pt-3 tw:sm:bg-brand-green-0 tw:sm:flex-row tw:sm:items-center tw:sm:justify-center tw:sm:p-8">
      <div className="tw:w-full tw:sm:max-w-md tw:sm:mx-auto">
        <div className="tw:bg-zinc-50 tw:rounded-md tw:sm:border tw:sm:border-zinc-200 tw:sm:shadow-sm tw:sm:p-6">
          <div className="tw:flex tw:justify-center tw:mb-2">
            <Logo className="logo" variant="color" />
          </div>
          <div
            className={
              referralProvider
                ? 'tw:flex tw:items-center tw:justify-between bg-neutral-100 tw:rounded-full tw:border tw:border-zinc-200 tw:px-2 tw:py-2 tw:mb-4 tw:text-xs'
                : 'tw:flex tw:justify-center tw:mb-4 tw:text-sm'
            }
          >
            {referralProvider && (
              <span className="tw:flex tw:items-center">
                <i className="fas fa-users tw:mr-1 text-color-primary" />
                You were referred by a colleague
              </span>
            )}
            <button
              type="button"
              onClick={() => setHowItWorksOpen(true)}
              className={clsx(
                'tw:!rounded-full tw:bg-stoplight-warning/80 tw:text-zinc-900 tw:border-0 tw:py-1 tw:cursor-pointer tw:text-sm',
                referralProvider ? 'tw:text-xs tw:px-2' : 'tw:px-3'
              )}
            >
              <i className="far fa-info-circle tw:mr-1 text-color-primary" />
              How it works
            </button>
          </div>
          <h5 className="tw:text-center tw:mb-2 tw:pb-2 text-color-primary-dark">
            New Provider Account
          </h5>
          <div className="tw:mb-1">
            <a
              className="tw:w-full tw:flex tw:items-center tw:justify-center tw:border tw:bg-white tw:border-teal-600 tw:rounded-lg tw:py-2 tw:px-3 tw:text-lg tw:font-medium tw:!no-underline tw:text-brand-green-0 hover:tw:bg-teal-50"
              href={`${window.GOOGLE_LOGIN_REDIRECT_URL}?user_type=${UserTypeEnum.PROVIDER}`}
            >
              <FcGoogle className="tw:mr-2 tw:mb-0.5" />
              <span>Create with Google</span>
            </a>
            <div className="tw:flex tw:items-center tw:my-3">
              <div className="tw:flex-1 tw:border-t tw:border-zinc-300" />
              <span className="tw:px-3 tw:text-sm tw:text-zinc-500">or setup manually</span>
              <div className="tw:flex-1 tw:border-t tw:border-zinc-300" />
            </div>
          </div>
          <form
            ref={formRef}
            onSubmit={handleSubmit(onSubmit)}
            className="tw:flex tw:flex-col new-account-form"
          >
            <FormFeedback serverErrors={(mutationRegister.error as any)?.body.non_field_errors} />
            <FormFeedback
              clientError={
                (errors as any).recaptcha_token
                  ? {
                      ...(errors as any).recaptcha_token,
                      message: `reCAPTCHA: ${(errors as any).recaptcha_token.message}`,
                    }
                  : undefined
              }
            />
            <div className="tw:grid tw:grid-cols-2 tw:gap-4">
              <FormTextField
                name="first_name"
                label="First Name"
                errors={errors}
                register={register}
                required
              />
              <FormTextField
                name="last_name"
                label="Last Name"
                errors={errors}
                register={register}
                required
              />
            </div>
            <FormInputMaskField
              name="phone_number"
              label="Phone Number"
              mask="(___) ___-____"
              replacement={{ _: /\d/ }}
              placeholder="(123) 456-7890"
              errors={errors}
              register={register}
              required
            />
            {userType === UserTypeEnum.HEALTHCARE_ENTITY_REP && (
              <>
                <FormTextField
                  name="healthcare_entity_rep__healthcare_entity__name"
                  label="Healthcare Entity Name"
                  errors={errors}
                  register={register}
                  required
                />
                <FormTextField
                  name="healthcare_entity_rep__title"
                  label="Job Title"
                  errors={errors}
                  register={register}
                  required
                />
              </>
            )}
            <FormTextField
              name="email"
              label="Email"
              errors={errors}
              register={register}
              required
            />
            <PasswordField
              name="password1"
              label="Password"
              errors={errors}
              register={register}
              required
            />
            <PasswordField
              name="password2"
              label="Confirm Password"
              errors={errors}
              register={register}
              required
            />
            <div className="tw:flex tw:items-start tw:mb-2">
              <FormCheckboxField
                name="text_opt_in"
                label=""
                errors={errors}
                register={register}
                className="tw:mb-0 tw:shrink-0 tw:inline-flex"
              />
              <small className="tw:text-zinc-500 tw:text-sm">
                I consent to receive notifications from Sendit about account activity via SMS.
                Message & data rates may apply. Message frequency depends on account activity. Reply
                STOP to opt out, HELP for help.{' '}
                <a
                  href="https://www.senditgigs.com/terms-of-service/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>{' '}
                &{' '}
                <a
                  href="https://www.senditgigs.com/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </small>
            </div>
            <div className="tw:mt-2">
              <Button
                type="submit"
                color="brandgreen1"
                className="tw:w-full tw:bg-teal-600 tw:text-white tw:py-3 tw:px-4 tw:rounded-lg tw:text-lg tw:font-medium tw:cursor-pointer hover:tw:bg-teal-700 disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
                onClick={() => handleSubmit(onSubmit)()}
                disabled={mutationRegister.isLoading}
              >
                Create Account
              </Button>
            </div>
            <div className="tw:text-center tw:my-3">
              Already have an account? <RouterLink to="/login">Sign in</RouterLink>
            </div>
            <div className="bg-primary-muted tw:rounded tw:px-3 tw:py-2 tw:mb-1 tw:mt-0 tw:text-xs">
              If you are a Healthcare Organization Representative, please{' '}
              <a href="https://www.senditgigs.com/get-in-touch/">contact us</a> to get started.
            </div>
          </form>
        </div>
      </div>
      <HowItWorksDialog open={howItWorksOpen} onClose={() => setHowItWorksOpen(false)} />
    </div>
  );
};
